import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  IconButton, 
  Box, 
  Grid,
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  TextField,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GridOnIcon from '@mui/icons-material/GridOn';
import GridOffIcon from '@mui/icons-material/GridOff';
import { BarChart } from '@mui/x-charts/BarChart';
import analyticsApi from '../../../services/analyticsapi';
import { getVendors } from '../../../services/productapi';

const VendorStatementModal = ({ open, onClose }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [vendorId, setVendorId] = useState('');
  const [showGrid, setShowGrid] = useState(true);
  const [chartType, setChartType] = useState('grouped'); // 'grouped' or 'stacked'
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [loadingVendors, setLoadingVendors] = useState(false);
  
  // Load initial data when modal opens
  useEffect(() => {
    if (open) {
      fetchVendors();
      fetchData(selectedYear, vendorId);
    }
  }, [open]);

  // Fetch vendors list
  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      const response = await getVendors();
      setVendors(response.data);
    } catch (err) {
      console.error("Error fetching vendors:", err);
    } finally {
      setLoadingVendors(false);
    }
  };

  // Fetch data based on selected year and vendor
  const fetchData = async (year, vendorId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsApi.getVendorStatementAnalytics(year, vendorId);
      setData(response.data);
    } catch (err) {
      console.error("Error fetching vendor statement data:", err);
      setError("Failed to load vendor statement data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle year selection change
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };
  
  // Handle vendor selection change
  const handleVendorChange = (event, newValue) => {
    setSelectedVendor(newValue);
    setVendorId(newValue ? newValue.id : '');
  };
  
  // Apply filters
  const handleApplyFilter = () => {
    fetchData(selectedYear, vendorId);
  };

  // Toggle chart type between grouped and stacked
  const handleChartTypeChange = (event) => {
    setChartType(event.target.checked ? 'stacked' : 'grouped');
  };

  // Convert data for chart display
  const prepareChartData = () => {
    if (!data) return null;
    
    const { months, data: chartData } = data;
    const debitData = [];
    const creditData = [];
    const monthsShort = months.map(month => month.substring(0, 3));
    
    for (let i = 1; i <= 12; i++) {
      const monthStr = i.toString();
      debitData.push(chartData.debit[monthStr] || 0);
      creditData.push(chartData.credit[monthStr] || 0);
    }
    
    return {
      debitData,
      creditData,
      monthsShort
    };
  };
  
  const chartData = prepareChartData();
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Vendor Statement Analytics
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="year-select-label">Year</InputLabel>
                <Select
                  labelId="year-select-label"
                  value={selectedYear}
                  onChange={handleYearChange}
                  label="Year"
                >
                  {[...Array(5)].map((_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Autocomplete
                sx={{ width: 300 }}
                options={vendors || []}
                getOptionLabel={(option) => option.name || ''}
                value={selectedVendor}
                onChange={handleVendorChange}
                loading={loadingVendors}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor Name (Optional)"
                    variant="outlined"
                    size="small"
                    placeholder="Select vendor"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingVendors ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={handleApplyFilter}
                  disabled={loading}
                >
                  Apply Filter
                </Button>
                <IconButton 
                  onClick={() => setShowGrid(!showGrid)}
                  color={showGrid ? 'primary' : 'default'}
                >
                  {showGrid ? <GridOnIcon /> : <GridOffIcon />}
                </IconButton>
                <FormControlLabel
                  control={
                    <Switch
                      checked={chartType === 'stacked'}
                      onChange={handleChartTypeChange}
                    />
                  }
                  label="Stacked"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Monthly Debit/Credit Analysis for {data.year}
              {selectedVendor && ` - Vendor: ${selectedVendor.name}`}
            </Typography>
            
            <Box sx={{ height: 400, mt: 4 }}>
              {chartData && (
                <BarChart
                  xAxis={[{ 
                    data: chartData.monthsShort,
                    scaleType: 'band',
                    label: 'Month'
                  }]}
                  series={[
                    {
                      data: chartData.debitData,
                      label: 'Debit',
                      color: theme.palette.success.main,
                      type: 'bar'
                    },
                    {
                      data: chartData.creditData,
                      label: 'Credit',
                      color: theme.palette.error.main,
                      type: 'bar'
                    }
                  ]}
                  height={400}
                  layout="vertical"
                  yAxis={[{ 
                    label: 'Amount (₹)',
                    grid: showGrid
                  }]}
                  legend={{ hidden: false }}
                  tooltip={{ trigger: 'item' }}
                  margin={{ left: 70 }}
                  slotProps={{
                    legend: {
                      direction: 'row',
                      position: { vertical: 'bottom', horizontal: 'middle' },
                      padding: 0,
                    }
                  }}
                  stackingOrder={chartType === 'stacked' ? 'ascending' : undefined}
                />
              )}
            </Box>
            
            {data && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom>
                    Summary
                </Typography>
                <Grid container spacing={3}>
                    {/* Debit Summary */}
                    <Grid item xs={12} md={6}>
                    <Paper
                        elevation={3}
                        sx={{
                        p: 3,
                        bgcolor: '#e8f5e9', 
                        borderLeft: '6px solid #388e3c', // strong green border
                        borderRadius: 2,
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ color: '#1b5e20' }} gutterBottom>
                        Debit Summary
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#1b5e20' }}>
                        ₹{' '}
                        {Object.values(data.data.debit)
                            .map((val) => Number(val) || 0)
                            .reduce((sum, val) => sum + val, 0)
                            .toLocaleString('en-IN')}
                        </Typography>
                    </Paper>
                    </Grid>

                    {/* Credit Summary */}
                    <Grid item xs={12} md={6}>
                    <Paper
                        elevation={3}
                        sx={{
                        p: 3,
                        bgcolor: '#ffebee', // light green
                        borderLeft: '6px solid #d32f2f', // strong green border
                        borderRadius: 2,
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ color: '#b71c1c' }} gutterBottom>
                        Credit Summary
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#b71c1c' }}>
                        ₹{' '}
                        {Object.values(data.data.credit)
                            .map((val) => Number(val) || 0)
                            .reduce((sum, val) => sum + val, 0)
                            .toLocaleString('en-IN')}
                        </Typography>
                    </Paper>
                    </Grid>
                </Grid>
                </Box>
            )}
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
            <Typography variant="body1" color="text.secondary">
              No data available. Please select filters and apply.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorStatementModal;
